// src/components/Header.js
import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../logo192.png';

import { useAuth } from 'react-oidc-context';
import './Header.css';

import { STOCKS as FALLBACK_STOCKS } from '../constants/stocks';
import { StockVisualizationContext } from '../contexts/StockVisualizationContext';
import { StockComparisonContext } from '../contexts/StockComparisonContext';
import { StockRatioVisualizationContext } from '../contexts/StockRatioVisualizationContext';
import { WatchlistContext } from '../contexts/StockWatchlistContext';
import PricingModal from './PricingModal';

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const isWatchlistPage = location.pathname === '/watchlist';
  const isLandingPage = location.pathname === '/';

  const { setSelectedStock: setVisualizationStock } = useContext(StockVisualizationContext);

  const { setSelectedSymbols } = useContext(StockComparisonContext);
  const setRatioStock = useContext(StockRatioVisualizationContext)?.setSelectedStock || null;

  const { add: addToWatchlist, symbols: watchlistSymbols } = useContext(WatchlistContext);

  const auth = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  // --- dynamic stock index (no api.js dependency) ---
  const [stockIndex, setStockIndex] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadStocks = async () => {
      const URL =
        'https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/list-stocks-search'; // your Lambda route

      try {
        const res = await fetch(URL, {
          headers: auth.user?.id_token
            ? { Authorization: `Bearer ${auth.user.id_token}` }
            : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        // normalize into [{symbol, name}]
        const normalized = (Array.isArray(json) ? json : json?.items || json || [])
          .map((it) => {
            if (typeof it === 'string') return { symbol: it, name: it };
            return {
              symbol: (it.symbol || it.ticker || it.code || '').toUpperCase(),
              name: it.name || it.companyName || it.title || it.symbol || '',
            };
          })
          .filter((x) => x.symbol);

        // de-dupe by symbol
        const seen = new Set();
        const unique = [];
        for (const row of normalized) {
          if (!seen.has(row.symbol)) {
            seen.add(row.symbol);
            unique.push(row);
          }
        }

        if (!cancelled) {
          setStockIndex(
            unique.map(({ symbol, name }) => ({
              symbol,
              name,
              searchableSymbol: symbol.toLowerCase(),
              searchableName: (name || symbol).toLowerCase(),
            }))
          );
        }
      } catch (e) {
        console.error('loadStocks failed, using fallback list:', e);
        if (!cancelled) {
          setStockIndex(
            FALLBACK_STOCKS.map(({ symbol, name }) => ({
              symbol,
              name,
              searchableSymbol: symbol.toLowerCase(),
              searchableName: name.toLowerCase(),
            }))
          );
        }
      }
    };

    loadStocks();
    return () => {
      cancelled = true;
    };
  }, [auth.user]);

  // search box state
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Check premium on login
  useEffect(() => {
    const checkPremium = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) return;
      try {
        const res = await fetch(
          'https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/check-premium-status',
          { headers: { Authorization: `Bearer ${auth.user.id_token}` } }
        );
        const data = await res.json();
        setIsPremium(!!data.isPremium);
      } catch (err) {
        console.error('Failed to check premium status:', err);
      }
    };
    checkPremium();
  }, [auth.isAuthenticated, auth.user]);

  // Re-check premium when the tab regains focus (after checkout/portal)
  useEffect(() => {
    const refresh = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) return;
      try {
        const res = await fetch(
          'https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/check-premium-status',
          { headers: { Authorization: `Bearer ${auth.user.id_token}` } }
        );
        const data = await res.json();
        setIsPremium(!!data.isPremium);
      } catch (err) {
        console.error('Failed to refresh premium status:', err);
      }
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [auth.isAuthenticated, auth.user]);

  // --- search ranking (startsWith first, then includes), limit 10 ---
  useEffect(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) {
      setSearchResults([]);
      return;
    }
    const startsWith = [];
    const includes = [];
    for (const item of stockIndex) {
      if (item.searchableSymbol.startsWith(q) || item.searchableName.startsWith(q)) {
        startsWith.push(item);
      } else if (item.searchableSymbol.includes(q) || item.searchableName.includes(q)) {
        includes.push(item);
      }
    }
    setSearchResults([...startsWith, ...includes].slice(0, 10));
  }, [searchInput, stockIndex]);

  const signOutRedirect = async () => {
    await auth.removeUser();
    const clientId = 'les81m9p8roonusucosdoe4td';
    const logoutUri = 'https://www.stockvisualize.com/';
    const domain = 'https://eu-west-2tofxpip5e.auth.eu-west-2.amazoncognito.com';
    const idToken = auth.user?.id_token;
    window.location.href = `${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}&id_token_hint=${idToken}`;
  };

  // >>>>>>>>>>>>>>>>>>>>>>> Code to add >>>>>>>>
  // const handleGoogleLogin = () => {
  //   const domain = 'https://eu-west-2tofxpip5e.auth.eu-west-2.amazoncognito.com';
  //   const clientId = 'les81m9p8roonusucosdoe4td';
  //   const redirect = 'https://www.stockvisualize.com/';
  //   const scope = 'openid+email+profile';

  //   window.location.href =
  //     `${domain}/oauth2/authorize?identity_provider=Google` +
  //     `&response_type=code&client_id=${clientId}` +
  //     `&redirect_uri=${encodeURIComponent(redirect)}` +
  //     `&scope=${scope}`;
  // };
  const handleGoogleLogin = () => {
    auth.signinRedirect({
      extraQueryParams: { identity_provider: 'Google' },
    });
  };
  // <<<<<<<<<<<<<<<<<<<<<<<< Code to add <<<<<<<<<<

  const handleBuyClick = () => setShowPricing(true);

  const handleSelectStock = (symbol) => {
    if (setVisualizationStock) setVisualizationStock(symbol);
    if (setRatioStock) setRatioStock(symbol);
    if (isWatchlistPage && !watchlistSymbols.includes(symbol)) {
      addToWatchlist(symbol);
    }
    setSelectedSymbols((prev) => {
      if (prev.includes(symbol)) return prev;
      const next = [...prev, symbol];
      return next.slice(0, 5);
    });
    setSearchInput('');
    setSearchResults([]);
    if (isLandingPage) {
      navigate('/visualization');
    }
  };

  if (auth.error) return <div>Error: {auth.error.message}</div>;

  return (
    <header className="header">
      <div className="header-left">
        <Link to="/">
          <img src={logo} alt="Logo" className="header-logo" />
        </Link>
      </div>

      <div className="header-center">
        <div className="header-search-container">
          <input
            type="text"
            className="header-search-input"
            placeholder="Search stocks by name or symbol (e.g., Apple or AAPL)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map(({ symbol, name }) => (
                <div
                  key={symbol}
                  className="search-result-item"
                  onClick={() => handleSelectStock(symbol)}
                >
                  {name} - {symbol}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        {/* Email icon aligned properly */}
        <div className="header-contact">
          <span className="contact-label">Contact us:</span>
          <a
            href="mailto:stock.visualise@gmail.com"
            className="header-mail-icon"
            aria-label="Email Support"
            target="_blank"
            rel="noopener noreferrer"
          >
            ✉️
          </a>
        </div>

        {auth.isAuthenticated ? (
          <>
            <div className="header-user-info">
              <span className="header-user">Hi, {auth.user?.profile?.email}</span>
              {isPremium ? (
                <div className="premium-badge-container">
                  <div className="premium-badge">You're a Premium Member!</div>
                  <a
                    href="https://billing.stripe.com/p/login/9B614mbwE9s2dpo8Goa3u00"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="manage-subscription-link"
                  >
                    Manage Subscription
                  </a>
                </div>
              ) : (
                <>
                  <button className="header-button premium" onClick={handleBuyClick}>
                    Buy Premium Access
                  </button>
                  <PricingModal
                    isOpen={showPricing}
                    onClose={() => setShowPricing(false)}
                    clientRefId={auth.user?.profile?.sub}
                    customerEmail={auth.user?.profile?.email}
                  />
                </>
              )}
            </div>
            <div className="header-actions">
              <button className="header-button login" onClick={signOutRedirect}>
                Log Out
              </button>
            </div>
          </>
        ) : (
        // >>>>>>>>>>>>>>>>>>>>>>> Code to add >>>>>>>>
          <>
            <button
              className="header-button login"
              onClick={() => auth.signinRedirect()}
            >
              Log In to search more stocks
            </button>
            <button
              className="header-button login google-login"
              onClick={handleGoogleLogin}
            >
              Continue with Google
            </button>
          </>
        // <<<<<<<<<<<<<<<<<<<<<<<< Code to add <<<<<<<<<<
        )}
      </div>
    </header>
  );
}
